import {Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { ThemePalette } from '@angular/material/core';
import { MatSelectChange } from '@angular/material/select';
import { CsvParserService } from '../../../../services/csv-parser.service';
import { HttpClient } from '@angular/common/http';
import {DateRange, StatisticsParams, Venue} from '../../../../utils/CommonInterfaces';
import { MatDialog } from '@angular/material/dialog';
import { HelpDialogComponent } from './help-dialog/help-dialog.component'; // Adjust path if needed
import * as moment from 'moment/moment';
import {NgStrapiAuthConfig} from '../../../../services/strapi/auth/ng-strapi-auth-config';
import {Moment} from 'moment/moment';
import {ShareDialogComponent} from './share-dialog/share-dialog.component';
import {RestaurantsService} from '../../../../services/restaurants.service';
import { DetailedViewDialogComponent } from './detailed-view-dialog/detailed-view-dialog.component';
import { getQpiColor, getQpiGradient } from './weekly-summary-colors';
import { MetricsTooltipService } from '../../../../services/metrics-tooltip.service';

// ========== INTERFACES ==========

// Base interface for common metric properties
interface BaseMetric {
  label: string;
  value: number;
}

// Specialized range interface for predictions
interface PredictionRange {
  low: number;
  high: number;
}

interface Range<T> {
  min: T;
  max: T;
}

interface SalesMetric extends BaseMetric {
  prediction: string;
  predicted: PredictionRange;
  demand_class: string;
  sales_mean: number;
  sales_std: number;
  deviation_percent: number;
  temperature: number;
  rain: number;
  clouds_all: number;
  wind_speed: number;
  passerby: number;
  threeWeekAvg: number;
  lastWeek?: number;
  lastYear?: number;
  passerbyLastWeek?: number;
  passerbyLastYear?: number;
}

interface QPIMetric extends BaseMetric {
  color: string;
  moreSales: string | null;
  efficiency_color: string;
  inefficiency_score: number;
  threeWeekAvg: number;
  ops_estimate_low: number;
  ops_estimate_high: number;
  durations_estimate_high: number;
  durations_estimate_low: number;
  bottleneck_estimate_high: number;
  bottleneck_estimate_low: number;
  bottleneck_deviation: number;
  occupancy_deviation: number;
  severity_durations: number;
  severity_bottleneck: number;
  severity_occupancy: number;
  lastWeek?: number;
  lastYear?: number;
}

interface ExperienceMetric extends BaseMetric {
  color: string;
  guest_experience_class: string;
  durations: number;
  durations_3week_avg: number;
  durations_range: Range<number>;
  duration_target: number;
  duration_deviation: number;
  bottleneck: number;
  bottleneck_range: Range<number>;
  durationsLastWeek?: number;
  durationsLastYear?: number;
}

interface StaffingMetric extends BaseMetric {
  flag: string | null;
  cost: number;
  col: number;
  col_3week_avg: number;
  color: string;
  staffing_class: string;
  staffing_flag: string | null;
  col_range: Range<number>;
  target_col: number;
  reasonable_labour_cost: number;
  severity: number;
  severity_pct: number;
  colLastWeek?: number;
  colLastYear?: number;
}

interface OperationalMetrics {
  queue_throughput: number;
  queue_throughput_sales: number;
  queue_load: number;
  ops_estimate_low: number;
  ops_estimate_high: number;
  ops_class: string;
  ops_estimate_low_v2: number;
  ops_estimate_high_v2: number;
  ops_class_2: string;
  covers: number;
  occupancy: number;
  labour_efficiency: number;
}

interface HistoricalStats {
  [key: string]: number;
}

export interface CellMetrics {
  humanDate: string;
  sales: SalesMetric;
  qpi: QPIMetric;
  experience: ExperienceMetric;
  staffing: StaffingMetric;
  operations: OperationalMetrics;
  stats: HistoricalStats;
  decision_str?: string;
}

interface WeekData {
  [day: string]: {
    [timeSlot: string]: CellMetrics;
  };
}

interface SummaryData {
  totalSales: number;
  total3WeekAvg: number;
  salesComparison: 'up' | 'down' | 'equal';
  avgSales: number;
  avgQPI: number;
  avgQPI3WeekAvg: number;
  qpiComparison: 'up' | 'down' | 'equal';
  avgQPILabel: string;
  avgExperienceDuration: number;
  avgExperienceDuration3WeekAvg: number;
  experienceComparison: 'up' | 'down' | 'equal';
  avgCoL: number;
  avgCoL3WeekAvg: number;
  staffingComparison: 'up' | 'down' | 'equal';
  salesByLabel: { [key: string]: number };
  qpiByLabel: { [key: string]: number };
  experienceByLabel: { [key: string]: number };
  staffingByLabel: { [key: string]: number };
  detailedStaffingByClass: { [key: string]: number }; // Add this new property
}

interface WeekRange {
  start: string;
  end: string;
}

interface ProcessedRow {
  date: Date;
  dayOfWeek: number;
  dayName: string;
  hour: number;
  minute: number;
  timeSlot: string;
  weekKey: string;
  [key: string]: any;
}

interface WeekOption {
  value: string;
  viewValue: string;
}

type ViewMode = 'week' | 'period';

interface FilterTab {
  name: string;
  settings: {
    sales: boolean;
    qpi: boolean;
    experience: boolean;
    staffing: boolean;
    moreSales: boolean;
    salesPrediction: boolean;
  };
}


@Component({
  selector: 'app-weekly-summary',
  templateUrl: './weekly-summary.component.html',
  styleUrls: ['./weekly-summary.component.scss'],
  animations: [
    trigger('tabTransition', [
      state('visible', style({ opacity: 1 })),
      state('hidden', style({ opacity: 0 })),
      transition('visible => hidden', animate('200ms ease-out')),
      transition('hidden => visible', animate('200ms ease-in'))
    ])
  ]
})
export class WeeklySummaryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() statisticsParams: StatisticsParams;
  @Input() venueData: Venue;
  @Input() code: String;

  @Output() weekDaysSelectedChange = new EventEmitter<{}>();
  @Output() openSidebarEvent = new EventEmitter();
  // @Output() selectedDateChange = new EventEmitter<{}>();


  // public showWeekDaysChips = true;
  // public isMobile = false;
  public weekDaysSelected: string[] = [];
  // public weekDaysSelectedOld: string[] = [];
  public dateRangeOg: { start: string; end: string, startRaw: Moment, endRaw: Moment };
  private apiUrl: string;
  public error = false;
  public errorMessage = 'An error occurred while fetching data';
  skipLoading: boolean;

  constructor(private csvParser: CsvParserService, private http: HttpClient,
              @Inject('config') private config: NgStrapiAuthConfig,
              public dialog: MatDialog, private restaurantsService: RestaurantsService,
              public tooltipService: MetricsTooltipService) {
    if (this.config && this.config.apiUrl) {
      this.apiUrl = this.config.apiUrl;
    } else {
      const err = '[NgStrapiAuth]: no api url provided';
      console.error(err);
      throw new Error('[NgStrapiAuth]: no api url provided');
    }
  }
  // ========== DATA PROPERTIES ==========
  csvData: any[] = [];
  processedData: WeekData | null = null;
  filteredData: WeekData | null = null;
  loading = false;
  selectedDay: string | null = null;
  selectedTimeSlot: string | null = null;
  summaryData: SummaryData | null = null;
  weekDays: string[] = [];
  timeSlots: string[] = [];
  weeks: string[] = [];
  weekOptions: WeekOption[] = [];
  selectedWeekIndex = 0;
  dateRange: WeekRange | null = null;
  showRawData = false;
  selectedCompareRange: 'threeWeekAvg' | 'lastWeek' | 'lastYear' = 'threeWeekAvg';
  // Debug logging toggle for compare/baseline logic
  private debugCompareLogs = false;

  private debugLog(context: string, payload?: any) {
    try {
      if (this.debugCompareLogs) {
        // Use console.log to ensure visibility in most environments
        // Keep payload shallow to avoid huge logs
        console.log('[WeeklySummary]', context, payload !== undefined ? payload : '');
      }
    } catch (e) { /* noop */ }
  }

  // ========== VIEW MODE ==========
  viewMode: ViewMode = 'period';
  standaloneView = false;

  // ========== SWIPE GESTURE PROPERTIES ==========
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private touchEndX: number = 0;
  private touchEndY: number = 0;
  private readonly SWIPE_THRESHOLD = 80; // minimum distance for swipe
  private readonly SWIPE_ANGLE_THRESHOLD = 45; // maximum angle for horizontal swipe
  private isSwiping: boolean = false;

  // ========== ANIMATION PROPERTIES ==========
  private isTransitioning: boolean = false;
  animationState: string = 'visible';

  // ========== PERIOD VIEW PROPERTIES ==========
  minDate: Date | null = null;
  maxDate: Date | null = null;
  periodStartDate: Date | null = null;
  periodEndDate: Date | null = null;

  // ========== MATERIAL PROPERTIES ==========
  color: ThemePalette = 'primary';

  // ========== COLOR MAP ==========

  // Define base colors with semantic names matching some keys
  opacity = 0.8;
  baseColors = {
    exceptional: 'rgba(33, 150, 243, 0.8)',
    good: 'rgba(76, 175, 80, 0.8)',
    not_ok: 'rgba(255, 235, 59, 0.8)',
    bad: 'rgba(255, 152, 0, 0.8)',
    veryBad: 'rgba(244, 67, 54, 0.8)',
    unlikely_understaffed: 'rgba(139, 195, 74, 0.8)',
    not_important: 'rgba(158, 158, 158, 0.8)',
  };
  colorKeys = Object.keys(this.baseColors);




// Create colorMap using spread and referencing baseColors
  colorMap = {
    ...this.baseColors,
    // Add references to existing colors
    ok: this.baseColors.good,
    adequate: this.baseColors.good,
    overstaffed: this.baseColors.veryBad,
    unlikely_overstaffed: this.baseColors.bad,
    understaffed: this.baseColors.exceptional
  };


  visibleMetrics = {
    sales: true,
    qpi: true,
    experience: false,
    staffing: false,
    moreSales: true,
    salesPrediction: true,
  };

  // ========== NEW FILTER TABS ==========
  filterTabs: FilterTab[] = [
    // { name: 'All', settings: { sales: true, qpi: true, experience: true, staffing: true, moreSales: true, salesPrediction: true } },
    { name: 'Sales', settings: { sales: true, qpi: true, experience: false, staffing: false, moreSales: true, salesPrediction: true } },
    { name: 'Guest Expereience', settings: {
      sales: false, qpi: true, experience: true, staffing: false, moreSales: true, salesPrediction: false } },
    { name: 'Staffing', settings: { sales: false, qpi: true, experience: false, staffing: true, moreSales: true, salesPrediction: false } }
  ];
  activeFilterTab: FilterTab = this.filterTabs[0];


  // Define slice orders for each chart type
  pieChartSliceOrders: { [key: string]: string[] } = {
    'sales': ['Overdelivering', 'Balanced',  'Underperforming'], // Default order
    'qpi': ['Green', 'Yellow', 'Orange', 'Red'], // Default order
    'experience': ['Exceptional', 'Ok', 'Bad'], // Default order
    'staffing': [ 'Understaffed', 'Adequate', 'Overstaffed'], // Default order
    'detailedStaffing': ['understaffed', 'unlikely_understaffed', 'adequate', 'unlikely_overstaffed', 'overstaffed'] // Add this line
  };

  openRightSidebar() {
    let data_fields = ['trends', 'weekSelect'];
    if (this.viewMode === 'week') {
      data_fields = ['weekSelect'];
    } else {
      data_fields = ['trends', 'weekSelect'];
    }
    this.openSidebarEvent.emit();
  }



  // ========== LIFECYCLE HOOKS ==========
  ngOnInit() {
    console.log('WeeklySummaryComponent ngOnInit()', this.activeFilterTab);
    this.restaurantsService.shareLinkSubject.subscribe(data => {
      this.openShareDialog();
    });
    this.restaurantsService.helpButtonSubject.subscribe(data => {
      this.openHelpDialog();
    });
    // Subscribe to compare range changes (default handled in component as threeWeekAvg)
    this.restaurantsService.compareRangeSubject.subscribe(range => {
      try {
        this.selectedCompareRange = range;
        console.log('selectedCompareRange', this.selectedCompareRange);
        // Recalculate views so summaries and cell trends match the new baseline
        if (this.viewMode === 'week') {
          this.updateWeekData();
        } else {
          this.applyFilters();
        }
      } catch (e) { /* noop */ }
    });
    console.log('ngOnInit: calling selectFilterTab with:', this.activeFilterTab);
    this.selectFilterTab(this.activeFilterTab);
    console.log('ngOnInit: visibleMetrics after selectFilterTab:', this.visibleMetrics);
  }
  ngOnChanges(change: SimpleChanges): void {
    const { statisticsParams, code } = change;
    if (code && code.currentValue) {
      this.standaloneView = true;
      this.fetchFixedData(code.currentValue);
    } else {
      console.log('statisticsParams', statisticsParams);
      console.log('venueData', this.venueData);

      if (statisticsParams && statisticsParams.currentValue.selectedDate) {
        const start = statisticsParams.currentValue.periodStartDate.clone();
        const end = statisticsParams.currentValue.periodEndDate.clone();

        this.dateRangeOg = {
          start: start.format('DD/MM/YYYY'),
          end: end.format('DD/MM/YYYY'),
          startRaw: start,
          endRaw: end,
        };
      }


      this.weekDaysSelected = [...this.statisticsParams.weekDaysSelected];
      console.log('this.dateRangeOg', this.dateRangeOg);
      this.fetchData();
    }

  }

  // ========== DATA COLLECTION ==========
  async fetchFixedData(code) {
    try {
      const result: any = await this.http
          .post(this.apiUrl + '/venues/getSharedEvaluationData', {
            'data': {'unique_code': code}
          })
          .toPromise();
      const {status, data, pageParams} = result;
      if (status === 'ok') {
        this.dateRangeOg = {
          start: moment(pageParams.start_range).format('DD/MM/YYYY'),
          end: moment(pageParams.end_range).format('DD/MM/YYYY'),
          startRaw: moment(pageParams.start_range),
          endRaw: moment(pageParams.end_range),
        };
        this.venueData = pageParams.venueData;
        this.csvData = data;
        this.processCSVData(data);
        this.loading = false;
        
        // Set body overflow-y to scroll for standalone view when data is fetched
        if (this.standaloneView) {
          document.body.style.overflowY = 'scroll';
        }
      } else {
        // this.loading = false;

        this.error = true;
      }
    } catch (e) {
      this.errorMessage = e;
      console.log('e', e);
      this.error = true;
    }
  }

  async fetchData() {
    if (!this.skipLoading) {
      this.loading = true;
      this.csvData = [];
      this.skipLoading = false;
    }

    const result: any = await this.http
        .post(this.apiUrl + '/venues/getEvaluationData', {
          'data': {'venueId': this.venueData.venueId, 'startDate': this.dateRangeOg.startRaw.format('YYYY-MM-DD'),
            'endDate': this.dateRangeOg.endRaw.format('YYYY-MM-DD')}
        })
        .toPromise();
    // await new Promise(resolve => setTimeout(resolve, 5000)); // One-liner delay for 2000ms
    const {status, data} = result;
    if (status === 'ok') {
      this.csvData = data;
      this.processCSVData(data);
      this.loading = false;
    }

    this.closeSlotDetails();
    return result;
  }

  // ========== FILE HANDLING ==========

  // Handle file upload
  handleFileUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) { return; }

    const file = input.files[0];
    this.loading = true;

    this.readFileAsText(file).then(fileContent => {
      const results = this.csvParser.parse(fileContent);
      this.csvData = results.data;
      // this.processCSVData(results.data);
      this.loading = false;
    }).catch(error => {
      console.error('Error reading file:', error);
      this.loading = false;
    });
  }

  // Load CSV from assets folder
  loadCSVFromAssets(filename: string = 'fully_processed_df_classified_fake.csv') {
    this.loading = true;

    const filePath = `assets/${filename}`;

    this.http.get(filePath, { responseType: 'text' })
        .subscribe({
          next: (fileContent) => {
            const results = this.csvParser.parse(fileContent);
            this.csvData = results.data;
            // this.processCSVData(results.data);
            this.loading = false;
          },
          error: (error) => {
            console.error('Error loading CSV from assets:', error);
            this.loading = false;
          }
        });
  }

  // Read file as text helper
  readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target?.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  }

  // ========== DATA PROCESSING ==========

  // Process CSV data
  processCSVData(data: any[]) {
    console.log('starting data, data', data);
    if (!data || data.length === 0) { return; }
    // Process date and group by week
    const parsedData = data.map(row => {
      const date = new Date(row.start_time_slot);

      const startOfWeek = new Date(date);
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
      startOfWeek.setDate(diff);
      const weekKey = startOfWeek.toISOString().split('T')[0];

      return {
        ...row,
        date,
        dayOfWeek: date.getDay(),
        dayName: this.getDayName(date.getDay()),
        hour: date.getHours(),
        minute: date.getMinutes(),
        timeSlot: this.formatTimeSlot(date),
        weekKey
      };
    });

    // Group data by weeks
    const weekGroups: { [weekKey: string]: ProcessedRow[] } = {};
    parsedData.forEach(row => {
      if (!weekGroups[row.weekKey]) {
        weekGroups[row.weekKey] = [];
      }
      weekGroups[row.weekKey].push(row);
    });

    const weeksList = Object.keys(weekGroups).sort();
    this.weeks = weeksList;

    // Create week options for dropdown
    this.weekOptions = weeksList.map((week, index) => {
      const range = this.getWeekDates(week);
      return {
        value: week,
        // viewValue: `Week ${index + 1}: ${range.start} - ${range.end}`
        viewValue: `${range.start} \n ${range.end}`
      };
    });

    // If no week is selected yet, select the first one
    if (this.selectedWeekIndex >= weeksList.length) {
      this.selectedWeekIndex = 0;
    }

    // Set date range bounds for period view
    this.setDateRangeBounds(parsedData);

    this.updateWeekData();
  }

  // Update the data for the currently selected week
  updateWeekData() {
    if (!this.csvData || this.csvData.length === 0 || this.weeks.length === 0) { return; }

    const selectedWeek = this.weeks[this.selectedWeekIndex];

    // Group data by week
    const weekGroups: { [weekKey: string]: ProcessedRow[] } = {};
    this.csvData.forEach(row => {
      const date = new Date(row.start_time_slot);

      const startOfWeek = new Date(date);
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
      startOfWeek.setDate(diff);
      const weekKey = startOfWeek.toISOString().split('T')[0];

      if (!weekGroups[weekKey]) {
        weekGroups[weekKey] = [];
      }

      weekGroups[weekKey].push({
        ...row,
        date,
        dayOfWeek: date.getDay(),
        dayName: this.getDayName(date.getDay()),
        hour: date.getHours(),
        minute: date.getMinutes(),
        timeSlot: this.formatTimeSlot(date),
        weekKey
      });
    });

    this.processWeekData(weekGroups[selectedWeek], selectedWeek);

    // Ensure visibleMetrics is synchronized with the active filter tab
    this.visibleMetrics = { ...this.activeFilterTab.settings };
    console.log('updateWeekData: synchronized visibleMetrics to:', this.visibleMetrics);

    this.applyFilters();
  }

  // Process data for a specific week
  processWeekData(weekData: ProcessedRow[], weekKey: string) {
    if (!weekData || weekData.length === 0) { return; }

    // Get unique days and time slots
    const uniqueDays = Array.from(new Set(weekData.map(row => row.dayName)))
        .map(dayName => {
          const row = weekData.find(r => r.dayName === dayName);
          return { dayName, dayOfWeek: row ? row.dayOfWeek : 0 };
        })
        .sort((a, b) => {
          // Convert day numbers to UK order (Monday=0, Sunday=6)
          const ukDayA = a.dayOfWeek === 0 ? 6 : a.dayOfWeek - 1;
          const ukDayB = b.dayOfWeek === 0 ? 6 : b.dayOfWeek - 1;
          return ukDayA - ukDayB;
        });

    const uniqueTimeSlots = Array.from(new Set(weekData.map(row => row.timeSlot)))
        .map(timeSlot => {
          const row = weekData.find(r => r.timeSlot === timeSlot);
          return {
            timeSlot,
            hour: row ? row.hour : 0,
            minute: row ? row.minute : 0
          };
        })
        .sort((a, b) => {
          // Compare hours first, then minutes
          if (a.hour !== b.hour) { return a.hour - b.hour; }
          return a.minute - b.minute;
        });

    const days = uniqueDays.map(day => day.dayName);
    const slots = uniqueTimeSlots.map(slot => slot.timeSlot);

    this.weekDays = days;
    this.timeSlots = slots;

    // Set date range for the selected week
    const weekDates = this.getWeekDates(weekKey);
    this.dateRange = weekDates;

    // Group data by day and time slot
    const groupedData: WeekData = {};

    days.forEach(day => {
      groupedData[day] = {};
      slots.forEach(slot => {
        const matchingRows = weekData.filter(row =>
            row.dayName === day && row.timeSlot === slot
        );

        if (matchingRows.length > 0) {
          const row = matchingRows[0];

          // Extract statistics for this row
          const stats: HistoricalStats = {};
          Object.keys(row).forEach(key => {
            // Find stats columns that follow pattern: {metric}_{timeframe}_{stat}
            if (/^[a-z_]+_(total|3week|6week)_(avg|std|q1|q3|iqr)$/.test(key)) {
              stats[key] = row[key] || 0;
            }
          });

          // Create the cell metrics using the helper method
          groupedData[day][slot] = this.createCellMetrics(row, stats);
        }
      });
    });

    // Calculate summary data
    const summary = this.calculateSummaryData(groupedData, days, slots);

    this.processedData = groupedData;
    this.filteredData = groupedData; // Initialize filtered data with all data
    this.summaryData = summary;
  }

  // Create CellMetrics object from row data
  createCellMetrics(row: any, stats: HistoricalStats): CellMetrics {
    // console.log('row', row);
    const salesValue = row.y || 0;
    const salesLow = row['yhat1_5'] || 0;
    const salesHigh = row['yhat1_95'] || 0;
    const salesMean = row.y_3week_avg || 0;
    const salesStd = row.y_3week_std || 0;
    const threeWeekAvg = row.y_3week_avg || 0;

    // Calculate deviation percentage
    let deviationPercent = 0;
    if (salesValue < salesLow) {
      deviationPercent = ((salesLow - salesValue) / salesLow) * 100;
    } else if (salesValue > salesHigh) {
      deviationPercent = ((salesValue - salesHigh) / salesHigh) * 100;
    }
    const valueReturn = {
      humanDate: new Date(row.start_time_slot).toLocaleString('en-US', {
        weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      }).replace(',', '').replace(',', ',') || '',
      sales: {
        label: this.mapDemandClass(row.demand_class),
        value: salesValue,
        predicted: {
          low: salesLow,
          high: salesHigh
        },
        prediction: row.yhat1         || '',
        demand_class: row.demand_class || '',
        sales_mean: salesMean,
        sales_std: salesStd,
        deviation_percent: deviationPercent,
        temperature: row.temp || 0,
        rain: row.rain_1h || 0,
        clouds_all: row.clouds_all || 0,
        wind_speed: row.wind_speed || 0,
        passerby: Math.round(row.passerby) || 0,
        threeWeekAvg: threeWeekAvg,
        lastWeek: typeof row.y_last_week === 'number' ? row.y_last_week : undefined,
        lastYear: typeof row.y_last_year === 'number' ? row.y_last_year : undefined,
        passerbyLastWeek: typeof row.passerby_last_week === 'number' ? Math.round(row.passerby_last_week) : undefined,
        passerbyLastYear: typeof row.passerby_last_year === 'number' ? Math.round(row.passerby_last_year) : undefined
      },
      qpi: {
        label: row.efficiency_color ? row.efficiency_color.charAt(0).toUpperCase() + row.efficiency_color.slice(1) : '',
        value: row.inefficiency_score ? 1 - row.inefficiency_score : 1,
        color: row.efficiency_color || 'None',
        moreSales: row.class_more_sales === 'None' ? null : row.class_more_sales,
        efficiency_color: row.efficiency_color || '',
        inefficiency_score: 1 - row.inefficiency_score || 1,
        threeWeekAvg: row.inefficiency_score_3week_avg ? 1 - row.inefficiency_score_3week_avg : 1,
        ops_estimate_low: row.ops_estimate_low_v2 || 0,
        ops_estimate_high: row.ops_estimate_high_v2 || 0,
        severity_bottleneck: row.severity_bottleneck * 100 || 0,
        severity_durations: row.severity_durations * 100 || 0,
        severity_occupancy: row.severity_occupancy * 100 || 0,
        durations_estimate_high: row.durations_estimate_high,
        durations_estimate_low: row.durations_estimate_low,
        bottleneck_estimate_high: row.bottleneck_estimate_high,
        bottleneck_estimate_low: row.bottleneck_estimate_low,
        bottleneck_deviation: 0,
        occupancy_deviation: 0,
        lastWeek: typeof row.inefficiency_score_last_week === 'number' ? 1 - row.inefficiency_score_last_week : undefined,
        lastYear: typeof row.inefficiency_score_last_year === 'number' ? 1 - row.inefficiency_score_last_year : undefined
      },
      experience: {
        label: this.mapExperienceClass(row.guest_experience_class),
        value: 0,
        color: this.getExperienceColor(this.mapExperienceClass(row.guest_experience_class)),
        guest_experience_class: row.guest_experience_class || '',
        durations: row.durations || 0,
        durations_3week_avg: row.durations_6week_avg || 0,
        durations_range: {
          min: row.durations_range_min || 0,
          max: row.durations_range_max || 0
        },
        duration_target: ((row.durations_range_min + row.durations_range_max) / 2) || 0,
        duration_deviation: ((row.durations - ((row.durations_range_min + row.durations_range_max) / 2)) /
            ((row.durations_range_min + row.durations_range_max) / 2)) * 100 || 0,
        bottleneck: row.bottleneck || 0,
        bottleneck_range: {
          min: row.bottleneck_estimate_low || 0,
          max: row.bottleneck_estimate_high || 0
        },
        durationsLastWeek: typeof row.durations_last_week === 'number' ? row.durations_last_week : undefined,
        durationsLastYear: typeof row.durations_last_year === 'number' ? row.durations_last_year : undefined
      },
      staffing: {
        label: this.mapStaffingClass(row.staffing_class),
        value: row.col || 0, // Using col (Cost of Labor) as the value for consistency
        color: this.staffingColor(row.staffing_class, row.staffing_flag),
        flag: row.staffing_flag,
        cost: row.labour_cost || 0,
        col: row.col || 0,
        col_3week_avg: row.col_3week_avg || 0,
        staffing_class: row.staffing_class || '',
        staffing_flag: row.staffing_flag,
        col_range: {
          min: row.col_range_min || 0,
          max: row.col_range_max || 0
        },
        target_col: row.target_col || 0,
        reasonable_labour_cost: row.reasonable_labour_cost || 0,
        severity: row.severity || 0,
        severity_pct: row.severity_pct || 0,
        colLastWeek: typeof row.col_last_week === 'number' ? row.col_last_week : undefined,
        colLastYear: typeof row.col_last_year === 'number' ? row.col_last_year : undefined
      },
      operations: {
        queue_throughput: row.queue_throughput || 0,
        queue_throughput_sales: row.queue_throughput_sales || 0,
        queue_load: row.queue_load || 0,
        ops_estimate_low: row.ops_estimate_low || 0,
        ops_estimate_high: row.ops_estimate_high || 0,
        ops_class: row.ops_class || '',
        ops_estimate_low_v2: row.ops_estimate_low_V2 || 0,
        ops_estimate_high_v2: row.ops_estimate_high_V2 || 0,
        ops_class_2: row.ops_class_2 || '',
        covers: row.covers || 0,
        occupancy: row.occupancy || 0,
        labour_efficiency: row.labour_efficiency || 0
      },
      stats: stats,
      decision_str: row.decision_str || ''
    };

    return valueReturn;
  }

  // Calculate summary metrics for the week
  calculateSummaryData(data: WeekData, days: string[], timeSlots: string[]): SummaryData {
    let totalSales = 0;
    let totalBaselineSales = 0;
    let totalEntries = 0;
    const qpiValues: number[] = [];
    const qpiBaselineValues: number[] = [];
    const experienceDurations: number[] = [];
    const experienceBaselineDurations: number[] = [];
    const colValues: number[] = [];
    const colBaselineValues: number[] = [];
    const salesByLabel = { Underperforming: 0, Balanced: 0, Overdelivering: 0 };
    const qpiByLabel = { Green: 0, Yellow: 0, Orange: 0, Red: 0 };
    const experienceByLabel = { Exceptional: 0, Ok: 0, Bad: 0 };
    const staffingByLabel = { Overstaffed: 0, Adequate: 0, Understaffed: 0 };
    // Add this object to track detailed staffing categories
    const detailedStaffingByClass = {
      overstaffed: 0,
      unlikely_overstaffed: 0,
      adequate: 0,
      unlikely_understaffed: 0,
      understaffed: 0
    };

    // Iterate through all data points
    days.forEach(day => {
      timeSlots.forEach(slot => {
        const metrics = data[day] && data[day][slot];
        if (metrics) {
          totalSales += metrics.sales.value;
          totalBaselineSales += this.getSalesBaselineValue(metrics);
          totalEntries++;
          qpiValues.push(metrics.qpi.value);
          qpiBaselineValues.push(this.getQpiBaselineValue(metrics));
          if (metrics.experience && typeof metrics.experience.durations === 'number') {
            experienceDurations.push(metrics.experience.durations);
            experienceBaselineDurations.push(this.getExperienceBaselineDuration(metrics));
          }
          if (metrics.staffing && typeof metrics.staffing.col === 'number') {
            colValues.push(metrics.staffing.col);
            colBaselineValues.push(this.getStaffingColBaselineValue(metrics));
          }

          // Count by label
          salesByLabel[metrics.sales.label as keyof typeof salesByLabel]++;
          qpiByLabel[metrics.qpi.label as keyof typeof qpiByLabel]++;
          experienceByLabel[metrics.experience.label as keyof typeof experienceByLabel]++;
          staffingByLabel[metrics.staffing.label as keyof typeof staffingByLabel]++;

          // Add this section to count by detailed staffing class
          const staffingClass = this.mapStaffingLabelToKey(metrics.staffing.label, metrics.staffing.flag);
          if (staffingClass && staffingClass in detailedStaffingByClass) {
            detailedStaffingByClass[staffingClass]++;
          }
        }
      });
    });

    // Calculate averages and percentages
    const avgSales = totalEntries > 0 ? totalSales / totalEntries : 0;
    const avgQPI = qpiValues.length > 0 ? qpiValues.reduce((sum, val) => sum + val, 0) / qpiValues.length : 0;
    const avgQPIBaseline = qpiBaselineValues.length > 0 ?
        qpiBaselineValues.reduce((sum, val) => sum + val, 0) / qpiBaselineValues.length : 0;
    const avgQPILabel = this.getQPILabel(avgQPI);
    const avgExperienceDuration = experienceDurations.length > 0 ?
        experienceDurations.reduce((sum, val) => sum + val, 0) / experienceDurations.length : 0;
    const avgExperienceDurationBaseline = experienceBaselineDurations.length > 0 ?
        experienceBaselineDurations.reduce((sum, val) => sum + val, 0) / experienceBaselineDurations.length : 0;
    const avgCoL = colValues.length > 0 ? colValues.reduce((sum, val) => sum + val, 0) / colValues.length : 0;
    const avgCoLBaseline = colBaselineValues.length > 0 ?
        colBaselineValues.reduce((sum, val) => sum + val, 0) / colBaselineValues.length : 0;

    // Calculate sales comparison
    let salesComparison: 'up' | 'down' | 'equal' = 'equal';
    if (totalSales > totalBaselineSales) {
      salesComparison = 'up';
    } else if (totalSales < totalBaselineSales) {
      salesComparison = 'down';
    }

    // Calculate QPI comparison
    let qpiComparison: 'up' | 'down' | 'equal' = 'equal';
    if (avgQPI > avgQPIBaseline) {
      qpiComparison = 'up';
    } else if (avgQPI < avgQPIBaseline) {
      qpiComparison = 'down';
    }

    // Calculate Experience comparison (lower duration is better)
    let experienceComparison: 'up' | 'down' | 'equal' = 'equal';
    if (avgExperienceDuration < avgExperienceDurationBaseline) {
      experienceComparison = 'up'; // Green - better performance (faster service)
    } else if (avgExperienceDuration > avgExperienceDurationBaseline) {
      experienceComparison = 'down'; // Red - worse performance (slower service)
    }

    // Calculate Staffing comparison (lower CoL is better)
    let staffingComparison: 'up' | 'down' | 'equal' = 'equal';
    if (avgCoL < avgCoLBaseline) {
      staffingComparison = 'up';
    } else if (avgCoL > avgCoLBaseline) {
      staffingComparison = 'down';
    }

    return {
      totalSales,
      total3WeekAvg: totalBaselineSales,
      salesComparison,
      avgSales,
      avgQPI,
      avgQPI3WeekAvg: avgQPIBaseline,
      qpiComparison,
      avgQPILabel,
      avgExperienceDuration,
      avgExperienceDuration3WeekAvg: avgExperienceDurationBaseline,
      experienceComparison,
      avgCoL,
      avgCoL3WeekAvg: avgCoLBaseline,
      staffingComparison,
      salesByLabel,
      qpiByLabel,
      experienceByLabel,
      staffingByLabel,
      detailedStaffingByClass
    };
  }

  // ========== NAVIGATION & UI INTERACTION ==========

  onMetricVisibilityChange(): void {
    // Force refresh of UI when metric visibility changes
    this.applyFilters();
  }

  selectFilterTab(tab: FilterTab) {
    if (this.isTransitioning || tab === this.activeFilterTab) {
      return; // Prevent multiple transitions
    }
    
    console.log('selectFilterTab called with:', tab);
    this.isTransitioning = true;
    
    // Store the new tab to switch to
    const newTab = tab;
    
    // Start fade out
    this.animationState = 'hidden';
    
    // Wait for fade out to complete, then change data
    setTimeout(() => {
      // Change the data after fade out
      this.activeFilterTab = newTab;
      this.visibleMetrics = { ...newTab.settings };
      console.log('visibleMetrics set to:', this.visibleMetrics);
      this.onMetricVisibilityChange();
      
      // Start fade in
      this.animationState = 'visible';
      
      // End transition after fade in completes
      setTimeout(() => {
        this.isTransitioning = false;
      }, 200);
    }, 200);
  }


  // Format date for display
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-UK', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  // Get week start and end dates
  getWeekDates(weekStartDate: string): WeekRange {
    const start = new Date(weekStartDate);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return {
      start: this.formatDate(start.toString()),
      end: this.formatDate(end.toString())
    };
  }

  // Navigate to previous week
  goToPreviousWeek() {
    if (this.selectedWeekIndex > 0) {
      this.selectedWeekIndex--;
      this.closeSlotDetails();
      this.updateWeekData();
    }
  }

  // Navigate to next week
  goToNextWeek() {
    if (this.selectedWeekIndex < this.weeks.length - 1) {
      this.selectedWeekIndex++;
      this.closeSlotDetails();
      this.updateWeekData();
    }
  }

  // Handle week selection change from dropdown
  onWeekSelectionChange(event: MatSelectChange) {
    const weekIndex = this.weeks.findIndex(week => week === event.value);
    if (weekIndex !== -1) {
      this.selectedWeekIndex = weekIndex;
      this.closeSlotDetails();
      this.updateWeekData();
    }
  }

  // Set date range bounds for period view
  setDateRangeBounds(parsedData: ProcessedRow[]) {
    if (!parsedData || parsedData.length === 0) {
      return;
    }

    // Get all dates from the data
    const allDates = parsedData.map(row => new Date(row.date));

    // Find min and max dates
    this.minDate = new Date(Math.min.apply(null, allDates.map(date => date.getTime())));
    this.maxDate = new Date(Math.max.apply(null, allDates.map(date => date.getTime())));

    // Default to most recent week for period view
    const defaultStartDate = new Date(this.maxDate);
    defaultStartDate.setDate(defaultStartDate.getDate() - 6);

    this.periodStartDate = this.minDate;
    this.periodEndDate = new Date(this.maxDate);
  }

  // Handle date range changes
  onDateRangeChanged() {
    if (this.periodStartDate && this.periodEndDate) {
      this.applyFilters();
    }
  }

  // Toggle view mode between week and period
  toggleViewMode(mode: ViewMode) {
    if (this.viewMode === mode) {
      return; // Already in this mode
    }

    this.viewMode = mode;
    this.closeSlotDetails();

    if (mode === 'week') {
      // Restore weekly view data
      this.updateWeekData();
    } else {
      // Ensure date range is set for period view
      if (!this.periodStartDate || !this.periodEndDate) {
        // Default to current week if not set
        const currentWeek = this.weeks[this.selectedWeekIndex];
        const weekStart = new Date(currentWeek);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        this.periodStartDate = weekStart;
        this.periodEndDate = weekEnd;
      }
    }

    this.applyFilters();
  }

  // Handle slot selection for detailed view
  handleSlotSelect(day: string, timeSlot: string) {
    if (this.isSmallScreen()) {
      // Open dialog on small screens
      const slot = this.filteredData?.[day]?.[timeSlot];
      if (slot) {
        this.dialog.open(DetailedViewDialogComponent, {
          width: '95vw',
          maxWidth: '600px',
          panelClass: 'detailed-view-dialog-container',
          data: {
            slot,
            visibleMetrics: {
              sales: true,
              qpi: true,
              experience: true,
              staffing: true,
              moreSales: true
            },
            showRawData: this.showRawData
          }
        });
      }
    } else {
      // Sidebar logic - only change selection if clicking on a different cell
      if (this.selectedDay !== day || this.selectedTimeSlot !== timeSlot) {
        this.selectedDay = day;
        this.selectedTimeSlot = timeSlot;
      }
      // If clicking on the same cell, do nothing - keep the details open
    }
  }

  // Close the slot details panel
  closeSlotDetails() {
    this.selectedDay = null;
    this.selectedTimeSlot = null;
  }

  // Toggle raw data display for debugging
  toggleRawData() {
    this.showRawData = !this.showRawData;
  }

  // Format raw data for display
  getFormattedRawData(data: any): string {
    return JSON.stringify(data, null, 2);
  }

  // ========== FILTER FUNCTIONS ==========

  // Apply filters to data
  applyFilters() {
    console.log('applyFilters()');
    if (!this.processedData) { return; }
    console.log('applyFilters()2');
    const filteredResult: WeekData = {};

    if (this.viewMode === 'week') {
      // Filter weekdays based on weekDaysSelected
      this.weekDays.forEach(day => {
        // Check if this day should be included based on weekDaysSelected
        const dayIndex = this.getDayIndexFromName(day);
        if (this.weekDaysSelected.length === 0 || this.weekDaysSelected.includes(dayIndex.toString())) {
          filteredResult[day] = {};
          this.timeSlots.forEach(slot => {
            // tslint:disable-next-line:no-non-null-assertion
            const cellData = this.processedData![day][slot];
            if (cellData) {
              filteredResult[day][slot] = cellData;
            }
          });
        }
      });
    } else {
      // Process period view - get data for date range
      if (!this.periodStartDate || !this.periodEndDate) {
        this.filteredData = {};
        return;
      }

      // Format dates for comparison
      const startDate = new Date(this.periodStartDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(this.periodEndDate);
      endDate.setHours(23, 59, 59, 999);

      // Get all rows within the date range
      const periodData = this.csvData.filter(row => {
        const rowDate = new Date(row.start_time_slot);
        return rowDate >= startDate && rowDate <= endDate;
      });

      // Process these rows
      const processedRows = periodData.map(row => {
        const date = new Date(row.start_time_slot);
        return {
          ...row,
          date,
          dayOfWeek: date.getDay(),
          dayName: this.getDayName(date.getDay()),
          hour: date.getHours(),
          minute: date.getMinutes(),
          timeSlot: this.formatTimeSlot(date),
          fullDate: this.formatDate(date.toString())
        };
      });

      // Filter rows based on weekDaysSelected
      const filteredProcessedRows = processedRows.filter(row => {
        if (this.weekDaysSelected.length === 0) {
          return true; // If no weekdays selected, show all
        }
        return this.weekDaysSelected.includes(row.dayOfWeek.toString());
      });

      // Get unique dates
      const uniqueDateMap = new Map();
      filteredProcessedRows.forEach(row => {
        const dateObj = row.date;
        const dateStr = dateObj.toISOString().split('T')[0];
        if (!uniqueDateMap.has(dateStr)) {
          uniqueDateMap.set(dateStr, {
            dateStr,
            dayName: row.dayName,
            fullDate: row.fullDate
          });
        }
      });

      // Sort dates and create display strings
      const uniqueDates = Array.from(uniqueDateMap.values())
          .sort((a, b) => a.dateStr.localeCompare(b.dateStr))
          .map(date => `${date.dayName}, ${date.fullDate}`);

      // Get unique time slots
      const uniqueTimeSlotSet = new Set<string>();
      filteredProcessedRows.forEach(row => {
        uniqueTimeSlotSet.add(row.timeSlot);
      });

      const uniqueTimeSlots = Array.from(uniqueTimeSlotSet)
          .sort((a, b) => {
            const [aHours, aMinutes] = a.split(':').map(Number);
            const [bHours, bMinutes] = b.split(':').map(Number);
            if (aHours !== bHours) { return aHours - bHours; }
            return aMinutes - bMinutes;
          });

      // Store these values temporarily (we'll filter empty days later)
      const allDays = uniqueDates;
      this.timeSlots = uniqueTimeSlots;

      // Group data by date and time slot
      allDays.forEach(dateStr => {
        filteredResult[dateStr] = {};

        uniqueTimeSlots.forEach(slot => {
          // Find matching row
          const matchingRow = filteredProcessedRows.find(row => {
            const rowDateStr = `${row.dayName}, ${row.fullDate}`;
            return rowDateStr === dateStr && row.timeSlot === slot;
          });

          if (matchingRow) {
            // Extract statistics for this row
            const stats: HistoricalStats = {};
            Object.keys(matchingRow).forEach(key => {
              if (/^[a-z_]+_(total|3week|6week)_(avg|std|q1|q3|iqr)$/.test(key)) {
                stats[key] = matchingRow[key] || 0;
              }
            });

            // Create cell metrics using the helper method
            const cellMetrics = this.createCellMetrics(matchingRow, stats);
            filteredResult[dateStr][slot] = cellMetrics;
          }
        });
      });

      // Remove days with no data after filtering
      const nonEmptyDays = allDays.filter(day => {
        if (!filteredResult[day]) { return false; }
        return Object.keys(filteredResult[day]).length > 0;
      });

      this.weekDays = nonEmptyDays;

      // Recalculate summary data for period view
      this.summaryData = this.calculateSummaryData(filteredResult, uniqueDates, uniqueTimeSlots);
      console.log('summaryData', this.summaryData);
    }

    this.filteredData = filteredResult;
  }

  // ========== WEEKDAY FILTERING ==========

  // Handle weekday selection changes
  onWeekDaysSelectedChange(newWeekDaysSelected: string[]) {
    this.weekDaysSelected = [...newWeekDaysSelected];
    this.applyFilters();

    // Emit the change to parent component
    const statisticsParamsToEmit = {
      ...this.statisticsParams,
      weekDaysSelected: this.weekDaysSelected,
    };
    this.weekDaysSelectedChange.emit(statisticsParamsToEmit);
  }

  // ========== HELPER FUNCTIONS ==========

  // Get day name from day index
  getDayName(dayIndex: number): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayIndex];
  }

  // Get day index from day name
  getDayIndexFromName(dayName: string): number {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days.indexOf(dayName);
  }

  // Format time slot for display
  formatTimeSlot(date: Date): string {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  // Map demand_class values to Sales Potential labels
  mapDemandClass(value: string): string {
    if (!value) { return 'None'; }

    switch (value.toLowerCase()) {
      case 'below':
        return 'Underperforming';
      case 'within':
        return 'Balanced';
      case 'above':
        return 'Overdelivering';
      default:
        return 'None';
    }
  }

  // Map guest_experience_class values to Guest Experience labels
  mapExperienceClass(value: string): string {
    if (!value) { return 'None'; }

    switch (value.toLowerCase()) {
      case 'above':
        return 'Bad';
      case 'within':
        return 'Ok';
      case 'below':
        return 'Exceptional';
      default:
        return 'None';
    }
  }

  // Map staffing_class values to Staffing labels
  mapStaffingClass(value: string): string {
    if (!value) { return 'None'; }

    switch (value.toLowerCase()) {
      case 'above':
        return 'Overstaffed';
      case 'within':
        return 'Adequate';
      case 'below':
        return 'Understaffed';
      default:
        return 'None';
    }
  }

  // Map staffing label and flag to colorMap key
  mapStaffingLabelToKey(label: string, flag: string | null): string {
    if (label === 'Overstaffed') {
      return flag === 'unlikely' ? 'unlikely_overstaffed' : 'overstaffed';
    } else if (label === 'Adequate') {
      return 'adequate';
    } else if (label === 'Understaffed') {
      return flag === 'unlikely' ? 'unlikely_understaffed' : 'understaffed';
    }
    return '';
  }

  // Get QPI label from color or calculate based on inefficiency score
  getQPILabel(score: number, color?: string): string {
    // If color is directly provided, use it
    if (color) {
      return color.charAt(0).toUpperCase() + color.slice(1);
    }

    // Otherwise calculate from score (thresholds from Python code)
    if (score === null || score === undefined) { return 'Green'; }

    if (score <= 0.03) { return 'Green'; }
    if (score <= 0.2) { return 'Yellow'; }
    if (score <= 0.4) { return 'Orange'; }
    return 'Red';
  }

  // Get experience color based on label
  getExperienceColor(label: string): string {
    switch (label) {
      case 'Exceptional': return this.colorMap.exceptional;
      case 'Ok': return this.colorMap.ok;
      case 'Bad': return this.colorMap.veryBad;
      default: return '#777777';
    }
  }

  // Get staffing color based on class and flag
  staffingColor(staffing_class: any, staffing_flag: any): string {

    if (staffing_class === 'above' && staffing_flag === 'None') {
      return this.colorMap.overstaffed;
    }
    if (staffing_class === 'above' && staffing_flag === 'unlikely') {
      return this.colorMap.unlikely_overstaffed;
    }
    if (staffing_class === 'within') {
      return this.colorMap.adequate;
    }
    if (staffing_class === 'below' && staffing_flag === 'unlikely') {
      return this.colorMap.unlikely_understaffed;
    }
    if (staffing_class === 'below' && staffing_flag === 'None') {
      return this.colorMap.understaffed;
    }
    return '#777777';
  }

  // Get cell background color based on type and label
  getBgColorSingle(type: string, label: string, flag?: string): string {
    if (type === 'sales') {
      switch (label) {
        case 'Underperforming': return this.colorMap.veryBad;
        case 'Balanced': return this.colorMap.ok;
        case 'Overdelivering': return this.colorMap.exceptional;
        default: return '';
      }
    } else if (type === 'qpi') {
      switch (label) {
        case 'Green': return this.colorMap.ok;
        case 'Yellow': return this.colorMap.not_ok;
        case 'Orange': return this.colorMap.bad;
        case 'Red': return this.colorMap.veryBad;
        default: return '';
      }
    } else if (type === 'experience') {
      return this.getExperienceColor(label);
    } else if (type === 'staffing') {
      // Handle all 5 types for detailed staffing
      switch (label) {
        case 'Understaffed': return this.colorMap.understaffed;
        case 'Unlikely Understaffed': return this.colorMap.unlikely_understaffed;
        case 'Adequate': return this.colorMap.adequate;
        case 'Unlikely Overstaffed': return this.colorMap.unlikely_overstaffed;
        case 'Overstaffed': return this.colorMap.overstaffed;
        default: return '#777777';
      }
    }
    return '';
  }

  // Get QPI color by label
  getQPIColorByLabel(label: string): string {
    switch (label) {
      case 'Green': return this.colorMap.ok;
      case 'Yellow': return this.colorMap.not_ok;
      case 'Orange': return this.colorMap.bad;
      case 'Red': return this.colorMap.veryBad;
      default: return '#777777';
    }
  }

  // Get staffing color by class
  getStaffingColorByClass(staffingClass: string): string {
    switch (staffingClass) {
      case 'overstaffed': return this.colorMap.overstaffed;
      case 'unlikely_overstaffed': return this.colorMap.unlikely_overstaffed;
      case 'adequate': return this.colorMap.adequate;
      case 'unlikely_understaffed': return this.colorMap.unlikely_understaffed;
      case 'understaffed': return this.colorMap.understaffed;
      default: return '#777777';
    }
  }

  // Get more sales potential color
  getMoreSalesColor(option: string): string {
    switch (option) {
      case 'unlikely': return this.colorMap.not_important; // Grey
      case 'possible': return this.colorMap.not_ok; // Orange
      case 'likely': return this.colorMap.veryBad; // Red
      default: return '#777777';
    }
  }

  // Format staffing label for display
  formatStaffingLabel(type: string): string {
    return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  }

  // Format more sales label for display
  formatMoreSalesLabel(option: string): string {
    return option.charAt(0).toUpperCase() + option.slice(1);
  }

  // Get high-contrast text color (white or black) based on background color
  getContrastTextColor(backgroundColor: string): string {
    // Check if color is in rgba format
    if (backgroundColor.startsWith('rgba')) {
      // Extract RGB components using regex
      const match = backgroundColor.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
      if (match) {
        // tslint:disable-next-line:radix
        const r = parseInt(match[1]);
        // tslint:disable-next-line:radix
        const g = parseInt(match[2]);
        // tslint:disable-next-line:radix
        const b = parseInt(match[3]);
        const alpha = parseFloat(match[4]);

        // Calculate luminance (accounting for opacity against white background)
        // Formula: alpha * color + (1 - alpha) * backgroundColor
        const r2 = alpha * r + (1 - alpha) * 255;
        const g2 = alpha * g + (1 - alpha) * 255;
        const b2 = alpha * b + (1 - alpha) * 255;

        const luminance = (0.299 * r2 + 0.587 * g2 + 0.114 * b2) / 255;

        // Use a more appropriate threshold for better contrast
        // Standard WCAG recommendation is around 0.55-0.6
        return luminance > 0.6 ? 'rgba(0, 0, 0, 0.87)' : 'white';
      }
    } else if (backgroundColor.startsWith('#')) {
      // Original hex handling code
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.6 ? 'rgba(0, 0, 0, 0.87)' : 'white';
    }

    // Default fallback
    return 'rgba(0, 0, 0, 0.87)';
  }

  // Get cell background style for grid cells - only QPI affects background
  getCellBackgroundStyle(sales: SalesMetric, qpi: QPIMetric): {[key: string]: string} {
    if (!this.visibleMetrics.qpi) {
      // QPI is hidden, use light grey
      return {
        background: 'rgba(230, 230, 230, 0.7)'
      };
    } else {
      // QPI is visible, use QPI color for background
      const qpiColor = getQpiColor(qpi.value * 100); // value is 0-1, convert to 0-100
      return {
        background: qpiColor
      };
    }
  }

  getQpiGradient(): string {
    return getQpiGradient();
  }

  // Get distribution percentage for bar charts
  getDistributionPercentage(count: number, total: number): number {
    return total > 0 ? (count / total) * 100 : 0;
  }

  // Format QPI value for display
  formatQpiValue(value: number): string {
    return (value * 100).toFixed(1) + '%';
  }

  // ========== METRIC DATA ACCESSORS ==========

  // Baseline helpers based on selectedCompareRange
  private getSalesBaselineValue(cellData: CellMetrics): number {
    if (!cellData || !cellData.sales) { return 0; }
    switch (this.selectedCompareRange) {
      case 'lastWeek':
        const salesLWRaw = cellData.sales.lastWeek;
        const sales3wRaw = cellData.sales.threeWeekAvg;
        const useLWSales = typeof salesLWRaw === 'number';
        const baselineSalesLW = useLWSales ? (salesLWRaw as number) : NaN;
        this.debugLog('baseline:sales:lastWeek', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.sales.value,
          candidates: { lastWeek: salesLWRaw, threeWeekAvg: sales3wRaw },
          checks: { isLastWeekNumber: useLWSales },
          chosen: useLWSales ? 'lastWeek' : 'none',
          baseline: baselineSalesLW
        });
        return baselineSalesLW;
      case 'lastYear':
        const salesLYRaw = cellData.sales.lastYear;
        const sales3wRaw2 = cellData.sales.threeWeekAvg;
        const useLYSales = typeof salesLYRaw === 'number';
        const baselineSalesLY = useLYSales ? (salesLYRaw as number) : NaN;
        this.debugLog('baseline:sales:lastYear', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.sales.value,
          candidates: { lastYear: salesLYRaw, threeWeekAvg: sales3wRaw2 },
          checks: { isLastYearNumber: useLYSales },
          chosen: useLYSales ? 'lastYear' : 'none',
          baseline: baselineSalesLY
        });
        return baselineSalesLY;
      case 'threeWeekAvg':
      default:
        const baselineSales3w = typeof cellData.sales.threeWeekAvg === 'number' ? cellData.sales.threeWeekAvg : NaN;
        this.debugLog('baseline:sales:threeWeekAvg', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.sales.value,
          source: 'threeWeekAvg',
          baseline: baselineSales3w
        });
        return baselineSales3w;
    }
  }

  private getQpiBaselineValue(cellData: CellMetrics): number {
    if (!cellData || !cellData.qpi) { return 0; }
    switch (this.selectedCompareRange) {
      case 'lastWeek':
        const qpiLWRaw = cellData.qpi.lastWeek;
        const qpi3wRaw = cellData.qpi.threeWeekAvg;
        const useLWQpi = typeof qpiLWRaw === 'number';
        const baselineQpiLW = useLWQpi ? (qpiLWRaw as number) : NaN;
        this.debugLog('baseline:qpi:lastWeek', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.qpi.value,
          candidates: { lastWeek: qpiLWRaw, threeWeekAvg: qpi3wRaw },
          checks: { isLastWeekNumber: useLWQpi },
          chosen: useLWQpi ? 'lastWeek' : 'none',
          baseline: baselineQpiLW
        });
        return baselineQpiLW;
      case 'lastYear':
        const qpiLYRaw = cellData.qpi.lastYear;
        const qpi3wRaw2 = cellData.qpi.threeWeekAvg;
        const useLYQpi = typeof qpiLYRaw === 'number';
        const baselineQpiLY = useLYQpi ? (qpiLYRaw as number) : NaN;
        this.debugLog('baseline:qpi:lastYear', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.qpi.value,
          candidates: { lastYear: qpiLYRaw, threeWeekAvg: qpi3wRaw2 },
          checks: { isLastYearNumber: useLYQpi },
          chosen: useLYQpi ? 'lastYear' : 'none',
          baseline: baselineQpiLY
        });
        return baselineQpiLY;
      case 'threeWeekAvg':
      default:
        const baselineQpi3w = typeof cellData.qpi.threeWeekAvg === 'number' ? cellData.qpi.threeWeekAvg : NaN;
        this.debugLog('baseline:qpi:threeWeekAvg', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.qpi.value,
          source: 'threeWeekAvg',
          baseline: baselineQpi3w
        });
        return baselineQpi3w;
    }
  }

  private getExperienceBaselineDuration(cellData: CellMetrics): number {
    if (!cellData || !cellData.experience) { return 0; }
    switch (this.selectedCompareRange) {
      case 'lastWeek':
        const expLWRaw = cellData.experience.durationsLastWeek;
        const exp3wRaw = cellData.experience.durations_3week_avg;
        const useLWExp = typeof expLWRaw === 'number';
        const baselineExpLW = useLWExp ? (expLWRaw as number) : NaN;
        this.debugLog('baseline:experience:lastWeek', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.experience.durations,
          candidates: { durationsLastWeek: expLWRaw, durations_3week_avg: exp3wRaw },
          checks: { isLastWeekNumber: useLWExp },
          chosen: useLWExp ? 'durationsLastWeek' : 'none',
          baseline: baselineExpLW
        });
        return baselineExpLW;
      case 'lastYear':
        const expLYRaw = cellData.experience.durationsLastYear;
        const exp3wRaw2 = cellData.experience.durations_3week_avg;
        const useLYExp = typeof expLYRaw === 'number';
        const baselineExpLY = useLYExp ? (expLYRaw as number) : NaN;
        this.debugLog('baseline:experience:lastYear', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.experience.durations,
          candidates: { durationsLastYear: expLYRaw, durations_3week_avg: exp3wRaw2 },
          checks: { isLastYearNumber: useLYExp },
          chosen: useLYExp ? 'durationsLastYear' : 'none',
          baseline: baselineExpLY
        });
        return baselineExpLY;
      case 'threeWeekAvg':
      default:
        const baselineExp3w = typeof cellData.experience.durations_3week_avg === 'number' ? cellData.experience.durations_3week_avg : NaN;
        this.debugLog('baseline:experience:threeWeekAvg', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.experience.durations,
          source: 'durations_3week_avg',
          baseline: baselineExp3w
        });
        return baselineExp3w;
    }
  }

  private getStaffingColBaselineValue(cellData: CellMetrics): number {
    if (!cellData || !cellData.staffing) { return 0; }
    switch (this.selectedCompareRange) {
      case 'lastWeek':
        const colLWRaw = cellData.staffing.colLastWeek;
        const col3wRaw = cellData.staffing.col_3week_avg;
        const useLWCol = typeof colLWRaw === 'number';
        const baselineColLW = useLWCol ? (colLWRaw as number) : NaN;
        this.debugLog('baseline:staffing:lastWeek', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.staffing.col,
          candidates: { colLastWeek: colLWRaw, col_3week_avg: col3wRaw },
          checks: { isLastWeekNumber: useLWCol },
          chosen: useLWCol ? 'colLastWeek' : 'none',
          baseline: baselineColLW
        });
        return baselineColLW;
      case 'lastYear':
        const colLYRaw = cellData.staffing.colLastYear;
        const col3wRaw2 = cellData.staffing.col_3week_avg;
        const useLYCol = typeof colLYRaw === 'number';
        const baselineColLY = useLYCol ? (colLYRaw as number) : NaN;
        this.debugLog('baseline:staffing:lastYear', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.staffing.col,
          candidates: { colLastYear: colLYRaw, col_3week_avg: col3wRaw2 },
          checks: { isLastYearNumber: useLYCol },
          chosen: useLYCol ? 'colLastYear' : 'none',
          baseline: baselineColLY
        });
        return baselineColLY;
      case 'threeWeekAvg':
      default:
        const baselineCol3w = typeof cellData.staffing.col_3week_avg === 'number' ? cellData.staffing.col_3week_avg : NaN;
        this.debugLog('baseline:staffing:threeWeekAvg', {
          selectedRange: this.selectedCompareRange,
          currentValue: cellData.staffing.col,
          source: 'col_3week_avg',
          baseline: baselineCol3w
        });
        return baselineCol3w;
    }
  }

  // Cell trend helpers for template
  isSalesAboveBaseline(cellData: CellMetrics): boolean {
    if (!cellData || !cellData.sales) { return false; }
    const baseline = this.getSalesBaselineValue(cellData);
    const val = cellData.sales.value;
    const cond = typeof val === 'number';
    const cmp = cond ? (val > baseline) : false;
    this.debugLog('trend:sales:above', { range: this.selectedCompareRange, value: val, baseline, checks: { isNumber: cond }, expression: `${val} > ${baseline}`, result: cmp });
    return cmp;
  }

  isSalesBelowBaseline(cellData: CellMetrics): boolean {
    if (!cellData || !cellData.sales) { return false; }
    const baseline = this.getSalesBaselineValue(cellData);
    const val = cellData.sales.value;
    const cond = typeof val === 'number';
    const cmp = cond ? (val < baseline) : false;
    this.debugLog('trend:sales:below', { range: this.selectedCompareRange, value: val, baseline, checks: { isNumber: cond }, expression: `${val} < ${baseline}`, result: cmp });
    return cmp;
  }

  isExperienceBetterThanBaseline(cellData: CellMetrics): boolean {
    if (!cellData || !cellData.experience) { return false; }
    const baseline = this.getExperienceBaselineDuration(cellData);
    const val = cellData.experience.durations;
    const cond = typeof val === 'number';
    const cmp = cond ? (val < baseline) : false;
    this.debugLog('trend:experience:better', { range: this.selectedCompareRange, value: val, baseline, checks: { isNumber: cond }, expression: `${val} < ${baseline}`, result: cmp });
    return cmp;
  }

  isExperienceWorseThanBaseline(cellData: CellMetrics): boolean {
    if (!cellData || !cellData.experience) { return false; }
    const baseline = this.getExperienceBaselineDuration(cellData);
    const val = cellData.experience.durations;
    const cond = typeof val === 'number';
    const cmp = cond ? (val > baseline) : false;
    this.debugLog('trend:experience:worse', { range: this.selectedCompareRange, value: val, baseline, checks: { isNumber: cond }, expression: `${val} > ${baseline}`, result: cmp });
    return cmp;
  }

  isStaffingBetterThanBaseline(cellData: CellMetrics): boolean {
    if (!cellData || !cellData.staffing) { return false; }
    const baseline = this.getStaffingColBaselineValue(cellData);
    const val = cellData.staffing.col;
    const cond = typeof val === 'number';
    const cmp = cond ? (val < baseline) : false;
    this.debugLog('trend:staffing:better', { range: this.selectedCompareRange, value: val, baseline, checks: { isNumber: cond }, expression: `${val} < ${baseline}`, result: cmp });
    return cmp;
  }

  isStaffingWorseThanBaseline(cellData: CellMetrics): boolean {
    if (!cellData || !cellData.staffing) { return false; }
    const baseline = this.getStaffingColBaselineValue(cellData);
    const val = cellData.staffing.col;
    const cond = typeof val === 'number';
    const cmp = cond ? (val > baseline) : false;
    this.debugLog('trend:staffing:worse', { range: this.selectedCompareRange, value: val, baseline, checks: { isNumber: cond }, expression: `${val} > ${baseline}`, result: cmp });
    return cmp;
  }

  // Get duration info
  getDuration(cellData: CellMetrics): string {
    const durations = cellData.experience.durations;
    if (durations) {
      return `${durations.toFixed(1)} seconds`;
    }
    return 'Not available';
  }

  // Get duration range info
  getDurationRange(cellData: CellMetrics, type?: string): string {
    let min = cellData.experience.durations_range.min;
    let max = cellData.experience.durations_range.max;

    if (type === 'estimate') {
      min = cellData.qpi.durations_estimate_low;
      max = cellData.qpi.durations_estimate_high;
    }
    if (min && max) {
      return `${min.toFixed(1)}-${max.toFixed(1)} seconds`;
    }
    return 'Not available';
  }

  // Get bottleneck info
  getBottleneck(cellData: CellMetrics): string {
    const bottleneck = cellData.experience.bottleneck;
    if (bottleneck) {
      return `${bottleneck.toFixed(1)} mins`;
    }
    return 'Not available';
  }

  // Get bottleneck range info
  getBottleneckRange(cellData: CellMetrics): string {
    const min = cellData.qpi.bottleneck_estimate_low;
    const max = cellData.qpi.bottleneck_estimate_high;
    if (min && max) {
      return `${min.toFixed(1)}-${max.toFixed(1)} mins`;
    }
    return 'Not available';
  }

  // Get labor cost info
  getLaborCost(cellData: CellMetrics): string {
    const cost = cellData.staffing.cost;
    if (cost) {
      return cost.toFixed(2);
    }
    return 'Not available';
  }

  // Get CoL (Cost of Labor) percentage
  getCoL(cellData: CellMetrics): string {
    const col = cellData.staffing.col;
    if (col) {
      return (col * 100).toFixed(1);
    }
    return 'Not available';
  }

  // Get CoL range
  getCoLRange(cellData: CellMetrics): string {
    const min = cellData.staffing.col_range.min;
    const max = cellData.staffing.col_range.max;
    if (min && max) {
      return `${(min * 100).toFixed(1)}-${(max * 100).toFixed(1)}%`;
    }
    return 'Not available';
  }

  // Get reasonable labor cost
  getReasonableLaborCost(cellData: CellMetrics): string {
    const cost = cellData.staffing.reasonable_labour_cost;
    if (cost) {
      return `£${cost.toFixed(2)}`;
    }
    return 'Not available';
  }

  // Get sales deviation from target range
  getSalesDeviation(sales: SalesMetric): string {
    const value = sales.value;
    const min = sales.predicted.low;
    const max = sales.predicted.high;

    if (value < min) { return `-£${(min - value).toFixed(2)}`; }
    if (value > max) { return `+£${(value - max).toFixed(2)}`; }
    return 'Within range';
  }

  // Calculate sales percentage position on range bar
  getSalesPercentage(sales: SalesMetric): number {
    const value = sales.value;
    const min = sales.predicted.low;
    const max = sales.predicted.high;
    const range = max - min;

    if (value <= min) { return 0; }
    if (value >= max) { return 100; }
    return ((value - min) / range) * 100;
  }

  // Get more sales explanation
  getMoreSalesExplanation(cellData: CellMetrics): string {
    const qpiLabel = cellData.qpi.label;
    const salesLabel = cellData.sales.label;
    const qpiValue = cellData.qpi.value;

    if (qpiValue <= 0.03) {
      return 'Queue is efficient, no opportunity for additional sales from queue improvements.';
    }

    if (salesLabel === 'Underperforming' && qpiValue > 0.03) {
      return `Sales below target and ${qpiLabel.toLowerCase()} queue performance (${(qpiValue * 100).toFixed(1)}%) indicates fixing inefficiencies would likely increase sales.`;
    }

    if (salesLabel === 'Balanced' && qpiValue > 0.03) {
      return `Sales at target level but ${qpiLabel.toLowerCase()} queue performance (${(qpiValue * 100).toFixed(1)}%) indicates possible opportunity for increased sales.`;
    }

    if (salesLabel === 'Overdelivering' && qpiValue > 0.03) {
      return `Sales above target despite ${qpiLabel.toLowerCase()} queue performance (${(qpiValue * 100).toFixed(1)}%), unlikely to gain more sales by fixing inefficiencies.`;
    }

    return 'No clear opportunity for additional sales identified.';
  }



  // Get total count for a distribution object
  getTotalCount(distribution: { [key: string]: number }): number {
    return Object.values(distribution).reduce((sum, count) => sum + count, 0);
  }

// Generate SVG path for a pie slice
  getPieSlicePath(category: string, distribution: { [key: string]: number }, chartType?: string): string {
    const total = this.getTotalCount(distribution);
    if (total === 0) { return ''; }

    // Calculate the angles for this slice using the proper ordering
    const startAngle = this.getCumulativeAngle(category, distribution, chartType);
    const angle = (distribution[category] / total) * 360;
    const endAngle = startAngle + angle;

    // Convert to radians
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    // Calculate points
    const radius = 50;
    const centerX = 50;
    const centerY = 50;

    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);

    // Create the SVG path
    const largeArcFlag = angle > 180 ? 1 : 0;

    return `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  }

// Reverse all slice orders (you can call this method on initialization if you want reversed order)
  reverseAllSliceOrders(): void {
    Object.keys(this.pieChartSliceOrders).forEach(chartType => {
      this.pieChartSliceOrders[chartType] = [...this.pieChartSliceOrders[chartType]].reverse();
    });
  }

// Reverse slice order for a specific chart type
  reverseSliceOrder(chartType: string): void {
    if (this.pieChartSliceOrders[chartType]) {
      this.pieChartSliceOrders[chartType] = [...this.pieChartSliceOrders[chartType]].reverse();
    }
  }

// Set a custom slice order for a specific chart type
  setCustomSliceOrder(chartType: string, customOrder: string[]): void {
    this.pieChartSliceOrders[chartType] = customOrder;
  }

// Calculate cumulative angle for ordering pie slices with custom order
  getCumulativeAngle(category: string, distribution: { [key: string]: number }, chartType: string): number {
    // Determine which chart type we're working with
    let chartTypeKey = '';
    if ('Underperforming' in distribution) { chartTypeKey = 'sales'; } else if ('Green' in distribution) { chartTypeKey = 'qpi'; } else if ('Exceptional' in distribution) { chartTypeKey = 'experience'; } else if ('Overstaffed' in distribution) { chartTypeKey = 'staffing'; } else if ('overstaffed' in distribution) { chartTypeKey = 'detailedStaffing'; } // Add this line

    // Use the specified chart type if provided
    if (chartType) { chartTypeKey = chartType; }

    // Get the ordered categories based on the chart type
    const orderedCategories = this.pieChartSliceOrders[chartTypeKey] || Object.keys(distribution);

    // Find the index of the current category in the ordered list
    const categoryIndex = orderedCategories.indexOf(category);

    // If it's the first category in the order, the cumulative angle is 0
    if (categoryIndex === 0) { return 0; }

    // Calculate cumulative angle from previous categories in the order
    let cumulativeAngle = 0;
    const total = this.getTotalCount(distribution);

    // Sum up the angles for all categories that come before this one in the order
    for (let i = 0; i < categoryIndex; i++) {
      const prevCategory = orderedCategories[i];
      if (prevCategory in distribution) {
        cumulativeAngle += (distribution[prevCategory] / total) * 360;
      }
    }

    return cumulativeAngle;
  }

  // Helper to map from staffing label to class for color lookup
  getStaffingClassFromLabel(label: string): string {
    switch (label) {
      case 'Overstaffed': return 'overstaffed';
      case 'Adequate': return 'adequate';
      case 'Understaffed': return 'understaffed';
      default: return '';
    }
  }

  // Get short day (Mon) and day.month (20.06) for period view day string
  getShortDay(day: string): string {
    // For period view, day is like "Monday, 20 June 2024"
    // For week view, it's just "Monday"
    if (day.includes(',')) {
      const d = day.split(',')[0].trim();
      return d.slice(0, 3);
    }
    return day.slice(0, 3);
  }

  getDayMonth(day: string): string {
    // For period view, day is like "Monday, 20 June 2024"
    if (day.includes(',')) {
      const dateStr = day.split(',')[1].trim(); // "20 June 2024"
      const [dayNum, monthName] = dateStr.split(' ');
      if (!dayNum || !monthName) { return ''; }
      // Convert month name to number
      const month = (new Date(`${monthName} 1, 2000`).getMonth() + 1).toString().padStart(2, '0');
      return `${dayNum}.${month}`;
    }
    return '';
  }

  openHelpDialog(): void {
    this.dialog.open(HelpDialogComponent, {
      width: '650px',
      autoFocus: false // To prevent focus on the close button initially
    });
  }
  openShareDialog(): void {
    this.dialog.open(ShareDialogComponent, {
      width: '650px',
      autoFocus: false,
      data: {
        venueName: this.venueData.name,
        venueId: this.venueData.venueId,
        dateStart: this.dateRangeOg.startRaw,
        dateEnd: this.dateRangeOg.endRaw
      }
    });
  }

  isSmallScreen(): boolean {
    return window.innerWidth <= 768;
  }

  ngOnDestroy() {
    try {
      this.restaurantsService.shareLinkSubject.unsubscribe();
      
      // Reset body overflow-y when component is destroyed
      if (this.standaloneView) {
        document.body.style.overflowY = '';
      }
    } catch (e) {
    }
  }

  extendDateRangePastByOneWeek() {
    const newDate = this.dateRangeOg.startRaw.subtract(7, 'days');

    const statisticsParamsToEmit = {
      selectedDate: newDate,
      periodStartDate: newDate,
      periodEndDate: this.dateRangeOg.endRaw,
      weekDaysSelected: this.weekDaysSelected,
    };

    this.skipLoading = true;

    this.weekDaysSelectedChange.emit(statisticsParamsToEmit);

  }

  // Navigate to previous week in period view
  goToPreviousWeekPeriod() {
    const weekStart = this.getNearestMonday(this.dateRangeOg.startRaw);
    const previousWeekStart = weekStart.clone().subtract(7, 'days');
    const previousWeekEnd = previousWeekStart.clone().add(6, 'days');
    let periodType = 'specific_week';
    if (previousWeekStart.isSame(moment().startOf('isoWeek'))) {
      periodType = 'current_week';
    } else if (previousWeekStart.isSame(moment().subtract(1, 'weeks').startOf('isoWeek'))) {
      periodType = 'last_week';
    }
    const statisticsParamsToEmit = {
      selectedDate: previousWeekStart,
      periodStartDate: previousWeekStart,
      periodEndDate: previousWeekEnd,
      periodType: periodType,
      weekDaysSelected: this.weekDaysSelected,
    };

    this.skipLoading = true;
    this.weekDaysSelectedChange.emit(statisticsParamsToEmit);
  }

  // Navigate to next week in period view
  goToNextWeekPeriod() {
    const weekStart = this.getNearestMonday(this.dateRangeOg.startRaw);
    const nextWeekStart = weekStart.clone().add(7, 'days');
    const nextWeekEnd = nextWeekStart.clone().add(6, 'days');
    // if the selected week is the current week, set the period type to current_week
    // if the selected week is the previous week, set the period type to last_week
    // if any other date range or week, set the period type to specific_week

    let periodType = 'specific_week';
    if (nextWeekStart.isSame(moment().startOf('isoWeek'))) {
      periodType = 'current_week';
    } else if (nextWeekStart.isSame(moment().subtract(1, 'weeks').startOf('isoWeek'))) {
      periodType = 'last_week';
    }

    const statisticsParamsToEmit = {
      selectedDate: nextWeekStart,
      periodStartDate: nextWeekStart,
      periodEndDate: nextWeekEnd,
      periodType: periodType,
      weekDaysSelected: this.weekDaysSelected,
    };

    this.skipLoading = true;
    this.weekDaysSelectedChange.emit(statisticsParamsToEmit);
  }

  // Helper method to get the nearest Monday to a given date
  private getNearestMonday(date: moment.Moment): moment.Moment {
    const dayOfWeek = date.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    if (dayOfWeek === 1) {
      // Already Monday
      return date.clone();
    } else if (dayOfWeek === 0) {
      // Sunday - go to previous Monday
      return date.clone().subtract(6, 'days');
    } else {
      // Tuesday through Saturday - go to previous Monday
      return date.clone().subtract(dayOfWeek - 1, 'days');
    }
  }

  // Check if next week button should be disabled
  isNextWeekDisabled(): boolean {
    if (!this.venueData?.latestDataDate || !this.dateRangeOg?.startRaw) {
      return false;
    }

    const weekStart = this.getNearestMonday(this.dateRangeOg.startRaw);
    const nextWeekStart = weekStart.clone().add(7, 'days');
    const latestDataDate = moment(this.venueData.latestDataDate);

    return nextWeekStart.isAfter(latestDataDate);
  }

  // ========== SWIPE GESTURE METHODS ==========
  
  onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 1) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.isSwiping = false;
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (event.touches.length === 1) {
      const currentX = event.touches[0].clientX;
      const currentY = event.touches[0].clientY;
      const deltaX = Math.abs(currentX - this.touchStartX);
      const deltaY = Math.abs(currentY - this.touchStartY);
      
      // If horizontal movement is greater than vertical, it's a potential swipe
      if (deltaX > deltaY && deltaX > 20) {
        this.isSwiping = true;
        event.preventDefault(); // Only prevent default if we're swiping
      }
    }
  }

  onTouchEnd(event: TouchEvent): void {
    if (event.changedTouches.length === 1 && this.isSwiping) {
      this.touchEndX = event.changedTouches[0].clientX;
      this.touchEndY = event.changedTouches[0].clientY;
      this.handleSwipe();
    }
    this.isSwiping = false;
  }

  private handleSwipe(): void {
    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    console.log('Swipe detected:', { deltaX, deltaY, distance, threshold: this.SWIPE_THRESHOLD });
    
    // Check if swipe distance is sufficient
    if (distance < this.SWIPE_THRESHOLD) {
      console.log('Swipe distance too small');
      return;
    }

    // Calculate angle to ensure it's a horizontal swipe
    const angle = Math.abs(Math.atan2(Math.abs(deltaY), Math.abs(deltaX)) * 180 / Math.PI);
    console.log('Swipe angle:', angle, 'threshold:', this.SWIPE_ANGLE_THRESHOLD);
    
    if (angle > this.SWIPE_ANGLE_THRESHOLD) {
      console.log('Swipe angle too steep');
      return; // Not a horizontal swipe
    }

    // Determine swipe direction and switch tabs
    if (deltaX > 0) {
      console.log('Swiping right - going to previous tab');
      this.swipeToPreviousTab();
    } else {
      console.log('Swiping left - going to next tab');
      this.swipeToNextTab();
    }
  }

  private swipeToPreviousTab(): void {
    const currentIndex = this.filterTabs.indexOf(this.activeFilterTab);
    if (currentIndex > 0) {
      this.selectFilterTab(this.filterTabs[currentIndex - 1]);
    }
  }

  private swipeToNextTab(): void {
    const currentIndex = this.filterTabs.indexOf(this.activeFilterTab);
    if (currentIndex < this.filterTabs.length - 1) {
      this.selectFilterTab(this.filterTabs[currentIndex + 1]);
    }
  }

  /**
   * Returns the total sales for a given day string (period view), summing all time slots.
   * Returns null if not in sales tab or data missing.
   */
  getTotalSalesForDay(day: string): number | null {
    if (!this.filteredData || !this.filteredData[day]) {
      return null;
    }
    let total = 0;
    for (const slot of Object.keys(this.filteredData[day])) {
      const cell = this.filteredData[day][slot];
      if (cell && cell.sales && typeof cell.sales.value === 'number') {
        total += cell.sales.value;
      }
    }
    return total;
  }

  /**
   * Returns a color string for the total sales for a day, comparing with 3-week average.
   * Green if above, red if below, gray if within 2% tolerance or undefined.
   */
  getTotalSalesColorForDay(day: string): string {
    if (!this.filteredData || !this.filteredData[day]) {
      return '#888';
    }
    let total = 0;
    let totalBaseline = 0;
    let baselineCount = 0;
    for (const slot of Object.keys(this.filteredData[day])) {
      const cell = this.filteredData[day][slot];
      if (cell && cell.sales && typeof cell.sales.value === 'number') {
        total += cell.sales.value;
        const base = this.getSalesBaselineValue(cell);
        if (Number.isFinite(base)) {
          totalBaseline += base;
          baselineCount++;
        }
      }
    }
    if (baselineCount === 0) return '#888';
    const diff = total - totalBaseline;
    const tolerance = totalBaseline * 0.02;
    if (Math.abs(diff) <= tolerance) {
      return '#888'; // gray for within 2%
    } else if (diff > 0) {
      return '#388e3c'; // green
    } else {
      return '#d32f2f'; // red
    }
  }

  /**
   * Returns the weighted average experience duration for a given day (sum of durations * covers / sum of covers).
   * Returns null if no data.
   */
  getWeightedAvgDurationForDay(day: string): number | null {
    if (!this.filteredData || !this.filteredData[day]) {
      return null;
    }
    let weightedSum = 0;
    let totalCovers = 0;
    for (const slot of Object.keys(this.filteredData[day])) {
      const cell = this.filteredData[day][slot];
      if (cell && cell.experience && typeof cell.experience.durations === 'number' && typeof cell.operations.covers === 'number') {
        weightedSum += cell.experience.durations * cell.operations.covers;
        totalCovers += cell.operations.covers;
      }
    }
    if (totalCovers === 0) return null;
    return Math.round(weightedSum / totalCovers);
  }

  /**
   * Returns the weighted average experience duration 3-week avg for a given day (sum of durations_3week_avg * covers / sum of covers).
   * Returns null if no data.
   */
  getWeightedAvgDuration3WeekForDay(day: string): number | null {
    if (!this.filteredData || !this.filteredData[day]) {
      return null;
    }
    let weightedSum = 0;
    let totalCovers = 0;
    for (const slot of Object.keys(this.filteredData[day])) {
      const cell = this.filteredData[day][slot];
      if (cell && cell.experience && typeof cell.operations.covers === 'number') {
        const baseline = this.getExperienceBaselineDuration(cell);
        if (Number.isFinite(baseline)) {
          weightedSum += baseline * cell.operations.covers;
        }
        totalCovers += cell.operations.covers;
      }
    }
    if (totalCovers === 0) return null;
    return Math.round(weightedSum / totalCovers);
  }

  /**
   * Returns a color string for the weighted avg duration for a day, comparing with 3-week avg.
   * Green if current < 3week avg, red if current > 3week avg, gray if equal or undefined.
   */
  getDurationColorForDay(day: string): string {
    const current = this.getWeightedAvgDurationForDay(day);
    const avg3w = this.getWeightedAvgDuration3WeekForDay(day);
    if (current == null || avg3w == null) return '#888';
    if (current < avg3w) return '#388e3c'; // green
    if (current > avg3w) return '#d32f2f'; // red
    return '#888'; // gray
  }

  /**
   * Returns the total staffing cost as a percentage of total sales for a given day.
   * Returns null if no data or total sales is zero.
   */
  getTotalStaffingCostPctForDay(day: string): number | null {
    if (!this.filteredData || !this.filteredData[day]) {
      return null;
    }
    let totalCost = 0;
    let totalSales = 0;
    for (const slot of Object.keys(this.filteredData[day])) {
      const cell = this.filteredData[day][slot];
      if (cell && typeof cell.staffing.cost === 'number') {
        totalCost += cell.staffing.cost;
      }
      if (cell && cell.sales && typeof cell.sales.value === 'number') {
        totalSales += cell.sales.value;
      }
    }
    if (totalSales === 0) return null;
    return Math.round((totalCost / totalSales) * 100);
  }

  /**
   * Returns the total staffing cost as a percentage of total sales for a given day, using 3-week avg values.
   * Returns null if no data or total sales is zero.
   */
  getTotalStaffingCostPct3WeekForDay(day: string): number | null {
    if (!this.filteredData || !this.filteredData[day]) {
      return null;
    }
    let totalCost = 0;
    let totalSalesBaseline = 0;
    for (const slot of Object.keys(this.filteredData[day])) {
      const cell = this.filteredData[day][slot];
      if (cell && typeof cell.staffing.cost === 'number') {
        // Use the same cost (no 3week avg for cost)
        totalCost += cell.staffing.cost;
      }
      if (cell && cell.sales) {
        const b = this.getSalesBaselineValue(cell);
        if (Number.isFinite(b)) {
          totalSalesBaseline += b;
        }
      }
    }
    if (totalSalesBaseline === 0) return null;
    return Math.round((totalCost / totalSalesBaseline) * 100);
  }

  /**
   * Returns a color string for the staffing cost percentage for a day, comparing with 3-week avg.
   * Green if current < 3week avg, red if current > 3week avg, gray if equal or undefined.
   */
  getStaffingCostPctColorForDay(day: string): string {
    const current = this.getTotalStaffingCostPctForDay(day);
    const avg3w = this.getTotalStaffingCostPct3WeekForDay(day);
    if (current == null || avg3w == null) return '#888';
    if (current < avg3w) return '#388e3c'; // green
    if (current > avg3w) return '#d32f2f'; // red
    return '#888'; // gray
  }
}
